import React, { useEffect, useMemo, useState, } from 'react';
import { Link, } from 'react-router-dom';
import { Button, } from 'reactstrap';
import { keyBy, orderBy, isEmpty, entries, omit, } from 'lodash';
import numeral from 'numeral';
import { format as formatDate, } from 'date-fns';
import { toast } from 'react-toastify';

import { functions } from '../../firebase';
import { idWithTenant, } from '../../shared/util';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useQueryParams from '../hooks/useQueryParams';
import usePagination from '../hooks/usePagination';
import Page from '../hocs/Page';
import ProductsTabs from '../ProductsTabs';
import ProgressButton from '../ProgressButton';
import MallProductMappingModal from '../modals/MallProductMappingModal';
import QuerySelector from '../QuerySelector';
import QueryInput from '../QueryInput';
import Pagination from '../Pagination';

const countPerPage = 100;
const pullShopifyProductsNow = functions.httpsCallable('pullShopifyProductsNow', { timeout: 550000 });
const statusLabels = {
  ACTIVE: { label: '販売中', color: 'success' },
  DRAFT: { label: '下書き', color: 'secondary' },
  ARCHIVED: { label: 'アーカイブ', color: 'secondary' },
};
const mappingOptions = [
  { label: '紐付け済み', value: 'mapped' },
  { label: '未紐付け', value: 'unmapped' },
];

export default Page(function MallProducts (props) {
  const { user, tenant, } = props;
  const queryParams = useQueryParams();
  const { keyword, mapping: [mappingValue] = [], } = queryParams;
  const shops = useCollectionSubscription(tenant.ref.collection('shops').where('mall', '==', 'shopify'), [tenant.id]);
  const sortedShops = orderBy(shops, _ => _.createdAt?.toDate(), 'asc');
  const shopsById = keyBy(shops, 'id');
  const shopOptions = sortedShops.map(_ => ({ label: _.name, value: _.id }));
  const shopId = queryParams.shopId?.[0];
  const shop = shopsById[shopId];
  const { items: mallProducts, isLoading } = useCollectionSubscription(shopId && tenant.ref.collection('mallProducts').where('shopId', '==', shopId), [tenant.id, shopId], { detail: true, initialItems: null, });
  const mappings = useCollectionSubscription(shopId && tenant.ref.collection('productMallMappings').where('shopId', '==', shopId), [tenant.id, shopId]);
  const mappingsByShopifySku = keyBy(mappings, 'shopifySku');
  const productChunks = useCollectionSubscription(shopId && tenant.ref.collection('productChunks'), [tenant.id, shopId]);
  const productsById = useMemo(() => keyBy(productChunks.flatMap(_ => entries(omit(_, ['id', 'ref'])).map(([k, v]) => ({ id: k, ...v, }))), 'id'), [productChunks]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [mappingMallProduct, setMappingMallProduct] = useState(null);

  const filteredMallProducts = useMemo(() => {
    return orderBy(mallProducts || [], ['productTitle', 'variantTitle'])
      .filter(_ => isEmpty(keyword) || [_.productTitle, _.variantTitle, _.sku, _.barcode].some(v => v?.toLowerCase().includes(keyword.toLowerCase())))
      .filter(_ => mappingValue == null || (mappingValue === 'mapped') === (!isEmpty(_.sku) && mappingsByShopifySku[_.sku] != null));
  }, [mallProducts, keyword, mappingValue, mappings]);
  const pagination = usePagination({ countPerPage, totalCount: filteredMallProducts.length, });
  const { offset, } = pagination;

  const sync = async () => {
    setIsSyncing(true);
    try {
      await pullShopifyProductsNow({ tenantId: tenant.id, shopId, });
    } finally {
      setIsSyncing(false);
    }
  };
  const onClickUnmap = async (mapping) => {
    if (!window.confirm(`LOGISP商品「${productsById[mapping.productId]?.sku ?? mapping.productId}」との紐付けを解除します。よろしいですか？`)) return;

    await mapping.ref.delete();
    toast.success('紐付けを解除しました');
  };
  const onClickSync = async () => {
    try {
      await sync();
      toast.success('Shopifyから商品を取得しました');
    } catch (e) {
      console.error(e);
      toast.error(e.message || '失敗しました', { autoClose: 10000 });
    }
  };

  useEffect(() => {
    user.ref.set({ preferences: { productsTab: '/mallProducts', } }, { merge: true });
  }, []);

  return (
    <div className="mall-products container-fluid">
      <div className="p-4 bg-white my-4">
        <ProductsTabs activeTab="mallProducts" shippingRequests={[]} tenant={tenant} />
        <div className="d-flex justify-content-start align-items-start mb-2 gap-1">
          <div>
            <QuerySelector width={200} paramName="shopId" options={shopOptions} label="店舗" selectorProps={{ isClearable: false }} />
            {shop == null && <div className="small text-danger mt-1">店舗を選択してください</div>}
          </div>
          <QueryInput width={300} paramName="keyword" label="商品名・SKU・バーコードで検索" />
          <QuerySelector width={200} paramName="mapping" options={mappingOptions} label="LOGISP商品との紐付け" />
        </div>
        {
          shop != null && !shop.shopifyAccessToken && (
            <div className="alert alert-warning">
              この店舗はShopifyと連携されていません。<Link to={`/settings/shops/${shop.id}`}>店舗設定</Link>からShopifyと連携してください。
            </div>
          )
        }
        {
          shop != null && (
            <>
              <div className="d-flex justify-content-end mb-1 gap-2 align-items-center">
                <Pagination {...pagination} />
                <div className="small text-muted">
                  {isSyncing ? (
                    <span><span className="fas fa-spin fa-spinner mr-1" />Shopifyから取得中...</span>
                  ) : (
                    <span>最終取得: {shop.shopifyProductsSyncedAt ? formatDate(shop.shopifyProductsSyncedAt.toDate(), 'yyyy/MM/dd HH:mm') : '未取得'}</span>
                  )}
                </div>
                <ProgressButton process={onClickSync} disabled={!shop.shopifyAccessToken || isSyncing}>
                  <span className="fas fa-sync mr-1" />
                  Shopifyから取得
                </ProgressButton>
              </div>
              <div className="mt-1 overflow-auto position-relative" style={{ zIndex: 0, }}>
                {
                  filteredMallProducts.length > 0 ? (
                    <table className="table table-sm table-bordered sticky-table">
                      <thead className="thead-light text-center">
                        <tr>
                          <th></th>
                          <th style={{ minWidth: 200 }}>商品名</th>
                          <th style={{ minWidth: 120 }}>バリエーション</th>
                          <th style={{ minWidth: 120 }}>SKU</th>
                          <th style={{ minWidth: 120 }}>バーコード</th>
                          <th>価格</th>
                          <th>Shopify在庫</th>
                          <th>ステータス</th>
                          <th style={{ minWidth: 150 }}>LOGISP商品</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          filteredMallProducts.slice(offset, offset + countPerPage).map((mallProduct) => {
                            const { id, imageUrl, productTitle, variantTitle, sku, barcode, price, inventoryQuantity, status, } = mallProduct;
                            const mapping = !isEmpty(sku) ? mappingsByShopifySku[sku] : null;

                            return (
                              <tr key={id}>
                                <td className="text-center">
                                  {imageUrl && <img src={imageUrl} alt="" style={{ width: 40, height: 40, objectFit: 'cover', }} />}
                                </td>
                                <td>{productTitle}</td>
                                <td>{variantTitle !== 'Default Title' && variantTitle}</td>
                                <td>{sku}</td>
                                <td>{barcode}</td>
                                <td className="text-right">{price != null && numeral(price).format('0,0[.]00')}</td>
                                <td className="text-right">{inventoryQuantity}</td>
                                <td className="text-center">
                                  <span className={`badge badge-${statusLabels[status]?.color || 'secondary'}`}>{statusLabels[status]?.label || status}</span>
                                </td>
                                <td>
                                  {
                                    mapping != null ? (
                                      <div className="d-flex justify-content-between align-items-start gap-1">
                                        <div>
                                          <div>{idWithTenant(tenant.ref.collection('products').doc(mapping.productId))}</div>
                                          <div className="small text-muted">{productsById[mapping.productId]?.sku}</div>
                                        </div>
                                        <Button size="sm" outline onClick={() => onClickUnmap(mapping)}>解除</Button>
                                      </div>
                                    ) : isEmpty(sku) ? (
                                      <div className="small text-muted" title="ShopifyでSKUを設定すると紐付けできます">SKU未設定のため紐付け不可</div>
                                    ) : (
                                      <div className="d-flex justify-content-between align-items-center gap-1">
                                        <span className="badge badge-warning">未紐付け</span>
                                        <Button size="sm" color="primary" onClick={() => setMappingMallProduct(mallProduct)}>紐付け</Button>
                                      </div>
                                    )
                                  }
                                </td>
                              </tr>
                            );
                          })
                        }
                      </tbody>
                    </table>
                  ) : (
                    <div>
                      {
                        (isLoading || isSyncing) ? (
                          <span className="fas fa-spin fa-spinner" />
                        ) : shop.shopifyAccessToken && shop.shopifyProductsSyncedAt == null ? (
                          <span className="text-danger">「Shopifyから取得」を押すと、Shopifyの商品を取得します</span>
                        ) : (
                          <span>商品はありません</span>
                        )
                      }
                    </div>
                  )
                }
              </div>
            </>
          )
        }
      </div>
      {
        mappingMallProduct != null && (
          <MallProductMappingModal isOpen tenant={tenant} shop={shop} mallProduct={mappingMallProduct} onClose={() => setMappingMallProduct(null)} />
        )
      }
    </div>
  );
});
